import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import BusinessProfileModal from './BusinessProfileModal';
import { ChevronDown, ChevronRight, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AccountingDashboard - ERP Phase 3.5
 * 
 * Read-only accounting with manual control.
 * Admin UI in English.
 * 
 * Key concepts:
 * - Last Renewal Date: Independent from monthly payment, tracks when client last renewed
 * - Month Status: Pending, Paid, Issue
 * - Delayed renewal ≠ unpaid (use Last Renewal Date for context)
 * 
 * UX Enhancements:
 * - Client Status Filter: Filter by active/inactive/with pending (default: active)
 * - Privacy Toggle: Hide financial totals by default
 */
const AccountingDashboard = () => {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, paid, issue
  
  // UX Enhancement: Client Status Filter (default: active)
  const [clientStatusFilter, setClientStatusFilter] = useState('active');
  const [showBizModal, setShowBizModal] = useState(false);
  
  // Collapsible summary section (collapsed by default, persisted)
  const [showSummarySection, setShowSummarySection] = useState(() => {
    try { return localStorage.getItem('accounting_summary_expanded') === 'true'; }
    catch { return false; }
  });

  const toggleSummarySection = () => {
    setShowSummarySection(prev => {
      const next = !prev;
      try { localStorage.setItem('accounting_summary_expanded', String(next)); } catch {}
      return next;
    });
  };

  const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-500/20 text-yellow-300' },
    { value: 'paid', label: 'Paid', color: 'bg-green-500/20 text-green-300' },
    { value: 'issue', label: 'Issue', color: 'bg-red-500/20 text-red-300' }
  ];
  
  // Client Status Filter Options
  // NOTE: "Active" includes both 'active' and 'onboarding' clients for accounting purposes
  const CLIENT_STATUS_OPTIONS = [
    { value: 'all', label: 'All Clients' },
    { value: 'active', label: 'Active + Onboarding' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'with_pending', label: 'With Pending' }
  ];

  // ERP Phase 4.6: Fetch dashboard data with complete state isolation
  const fetchDashboard = useCallback(async (targetMonth) => {
    // CRITICAL: Clear ALL month-specific data immediately
    // This prevents any visual "leakage" of old month's data
    setData(null);
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/accounting/dashboard?month=${targetMonth}`);
      // Only update state if the response is for the current month
      // This prevents race conditions when quickly changing months
      setData(response.data);
    } catch (error) {
      console.error('Error fetching accounting dashboard:', error);
      // On error, ensure data is cleared to avoid showing stale data
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // ERP Phase 4.6: Re-fetch whenever month changes
  useEffect(() => {
    fetchDashboard(month);
  }, [month, fetchDashboard]);

  // ERP Phase 4.6: Month navigation with full state reset
  // This helper is kept for potential programmatic use
  const navigateMonth = (direction) => {
    // CRITICAL: Clear data BEFORE changing month to ensure no stale data is visible
    setData(null);
    setFilter('all'); // Reset filter on month change
    
    const [year, monthNum] = month.split('-').map(Number);
    let newYear = year;
    let newMonth = monthNum + direction;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const formatMonthYear = (monthStr) => {
    const [year, monthNum] = monthStr.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
  };

  const handleStatusChange = async (clientId, newStatus) => {
    setUpdating(clientId);
    try {
      await axios.put(`${API}/accounting/${clientId}/${month}`, {
        status: newStatus
      });
      fetchDashboard(month);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status');
    } finally {
      setUpdating(null);
    }
  };

  const handlePaymentDateChange = async (clientId, newDate) => {
    setUpdating(clientId);
    try {
      // Send empty string to clear, or the date value
      await axios.put(`${API}/accounting/${clientId}/${month}`, {
        payment_date: newDate || ''  // Empty string to clear
      });
      fetchDashboard(month);
    } catch (error) {
      console.error('Error updating payment date:', error);
      alert('Error updating payment date');
    } finally {
      setUpdating(null);
    }
  };

  const handleRenewalDateChange = async (clientId, newDate) => {
    setUpdating(clientId);
    try {
      await axios.put(`${API}/accounting/client/${clientId}/renewal`, {
        last_renewal_date: newDate || null
      });
      fetchDashboard(month);
    } catch (error) {
      console.error('Error updating renewal date:', error);
      alert('Error updating renewal date');
    } finally {
      setUpdating(null);
    }
  };

  const handleNotesChange = async (clientId, newNotes) => {
    setUpdating(clientId);
    try {
      // Send empty string or the value - both are valid
      await axios.put(`${API}/accounting/${clientId}/${month}`, {
        notes: newNotes || ''  // Empty string instead of null to clear
      });
      // Refetch to ensure UI is in sync
      fetchDashboard(month);
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Error updating notes');
    } finally {
      setUpdating(null);
    }
  };

  // Format month for display (English)
  const formatMonthDisplay = (monthStr) => {
    const [year, monthNum] = monthStr.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // ERP Phase 4.6: Navigate months with complete state isolation
  // Each navigation clears data first to prevent any visual leakage
  const goToPreviousMonth = () => {
    // CRITICAL: Clear data BEFORE changing month
    setData(null);
    setFilter('all');
    
    const [year, monthNum] = month.split('-').map(Number);
    const newDate = new Date(year, monthNum - 2, 1);
    setMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
  };

  const goToNextMonth = () => {
    // CRITICAL: Clear data BEFORE changing month
    setData(null);
    setFilter('all');
    
    const [year, monthNum] = month.split('-').map(Number);
    const newDate = new Date(year, monthNum, 1);
    setMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
  };

  const goToCurrentMonth = () => {
    // CRITICAL: Clear data BEFORE changing month
    setData(null);
    setFilter('all');
    
    const now = new Date();
    setMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  };

  // Filter clients by both client status and month status
  // Step 1: Apply client status filter first
  // NOTE: "Active" filter includes both 'active' AND 'onboarding' clients (considered active for accounting)
  const clientStatusFiltered = useMemo(() => {
    if (!data?.clients) return [];
    
    return data.clients.filter(client => {
      if (clientStatusFilter === 'all') return true;
      // Active filter: includes both 'active' AND 'onboarding' status
      if (clientStatusFilter === 'active') {
        return client.client_status === 'active' || client.client_status === 'onboarding';
      }
      // Inactive filter: only 'inactive' status
      if (clientStatusFilter === 'inactive') return client.client_status === 'inactive';
      // With Pending: any client status but must have pending payment
      if (clientStatusFilter === 'with_pending') return client.month_status === 'pending';
      return true;
    });
  }, [data?.clients, clientStatusFilter]);
  
  // Step 2: Apply month status filter
  const filteredClients = useMemo(() => {
    if (filter === 'all') return clientStatusFiltered;
    return clientStatusFiltered.filter(client => client.month_status === filter);
  }, [clientStatusFiltered, filter]);
  
  // Dynamic summary based on filtered clients
  const filteredSummary = useMemo(() => {
    if (!clientStatusFiltered.length) {
      return {
        total_clients: 0,
        paid: 0,
        pending: 0,
        issue: 0,
        received_total: 0,
        expected_total: 0
      };
    }
    
    const paid = clientStatusFiltered.filter(c => c.month_status === 'paid').length;
    const pending = clientStatusFiltered.filter(c => c.month_status === 'pending').length;
    const issue = clientStatusFiltered.filter(c => c.month_status === 'issue').length;
    
    const received_total = clientStatusFiltered
      .filter(c => c.month_status === 'paid')
      .reduce((sum, c) => sum + (c.monthly_fee || 0), 0);
    
    const expected_total = clientStatusFiltered
      .reduce((sum, c) => sum + (c.monthly_fee || 0), 0);
    
    return {
      total_clients: clientStatusFiltered.length,
      paid,
      pending,
      issue,
      received_total,
      expected_total
    };
  }, [clientStatusFiltered]);

  // Get status badge color
  const getStatusColor = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    return option?.color || 'bg-gray-500/20 text-gray-300';
  };

  // Format booking mode
  const formatBookingMode = (mode) => {
    if (mode === 'monthly_fixed') return 'Monthly Fixed';
    if (mode === 'booking_flex') return 'Session Package';
    return mode || '-';
  };

  // Format client status
  const formatClientStatus = (status) => {
    if (status === 'active') return 'Active';
    if (status === 'onboarding') return 'Onboarding';
    if (status === 'inactive') return 'Inactive';
    return status || '-';
  };

  // ERP Phase 4.6: Show loading state whenever loading OR data is null
  // This ensures we never show stale data during month transitions
  if (loading || !data) {
    return (
      <div className="space-y-6" data-testid="accounting-dashboard-loading">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">💰 Accounting</h2>
        </div>

        {/* Month Selector - Keep visible during loading for context */}
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={goToPreviousMonth}
              disabled={loading}
              className="px-4 py-2 bg-blue-500/20 text-blue-300 rounded-lg hover:bg-blue-500/30 transition disabled:opacity-50"
              data-testid="prev-month-btn"
            >
              ← Previous
            </button>
            
            <div className="text-center">
              <h3 className="text-xl font-bold text-white">{formatMonthDisplay(month)}</h3>
              <button
                onClick={goToCurrentMonth}
                disabled={loading}
                className="text-sm text-blue-400 hover:text-blue-300 mt-1 disabled:opacity-50"
              >
                Go to current month
              </button>
            </div>
            
            <button
              onClick={goToNextMonth}
              disabled={loading}
              className="px-4 py-2 bg-blue-500/20 text-blue-300 rounded-lg hover:bg-blue-500/30 transition disabled:opacity-50"
              data-testid="next-month-btn"
            >
              Next →
            </button>
          </div>
        </div>

        {/* Loading Indicator */}
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
            <p className="text-blue-300">Loading {formatMonthDisplay(month)} data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">💰 Accounting</h2>
        <button
          onClick={() => setShowBizModal(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-500/20 text-blue-300 rounded-lg hover:bg-blue-500/30 transition"
          data-testid="invoice-settings-btn"
          title="Invoice / Business settings"
        >
          <Settings className="w-4 h-4" /> Invoice Settings
        </button>
      </div>

      {showBizModal && <BusinessProfileModal onClose={() => setShowBizModal(false)} />}

      {/* Month Selector */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={goToPreviousMonth}
            className="px-4 py-2 bg-blue-500/20 text-blue-300 rounded-lg hover:bg-blue-500/30 transition"
            data-testid="prev-month-btn"
          >
            ← Previous
          </button>
          
          <div className="text-center">
            <h3 className="text-xl font-bold text-white">{formatMonthDisplay(month)}</h3>
            <button
              onClick={goToCurrentMonth}
              className="text-sm text-blue-400 hover:text-blue-300 mt-1"
            >
              Go to current month
            </button>
          </div>
          
          <button
            onClick={goToNextMonth}
            className="px-4 py-2 bg-blue-500/20 text-blue-300 rounded-lg hover:bg-blue-500/30 transition"
            data-testid="next-month-btn"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Collapsible Summary + Filters Section */}
      {data?.summary && (
        <div className="space-y-3">
          {/* Toggle Button */}
          <div className="flex justify-end">
            <button
              onClick={toggleSummarySection}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                showSummarySection
                  ? 'bg-blue-500/30 text-blue-200 hover:bg-blue-500/40'
                  : 'bg-gray-500/20 text-gray-300 hover:bg-gray-500/30'
              }`}
              data-testid="toggle-summary-btn"
            >
              {showSummarySection ? (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Hide Totals
                </>
              ) : (
                <>
                  <ChevronRight className="w-4 h-4" />
                  Show Totals
                </>
              )}
            </button>
          </div>

          {/* Collapsible Content */}
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              maxHeight: showSummarySection ? '500px' : '0',
              opacity: showSummarySection ? 1 : 0,
            }}
          >
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
              <div className="glass-card p-4 text-center">
                <div className="text-blue-300 text-sm">Total Clients</div>
                <div className="text-2xl font-bold text-white">{filteredSummary.total_clients}</div>
                {clientStatusFilter !== 'all' && (
                  <div className="text-xs text-blue-400 mt-1">
                    ({clientStatusFilter === 'with_pending' ? 'with pending' : clientStatusFilter === 'active' ? 'active + onboarding' : clientStatusFilter})
                  </div>
                )}
              </div>
              <div className="glass-card p-4 text-center bg-green-500/10">
                <div className="text-green-300 text-sm">Paid</div>
                <div className="text-2xl font-bold text-green-300">{filteredSummary.paid}</div>
              </div>
              <div className="glass-card p-4 text-center bg-yellow-500/10">
                <div className="text-yellow-300 text-sm">Pending</div>
                <div className="text-2xl font-bold text-yellow-300">{filteredSummary.pending}</div>
              </div>
              <div className="glass-card p-4 text-center bg-red-500/10">
                <div className="text-red-300 text-sm">Issues</div>
                <div className="text-2xl font-bold text-red-300">{filteredSummary.issue}</div>
              </div>
              <div className="glass-card p-4 text-center">
                <div className="text-blue-300 text-sm">Collected</div>
                <div className="text-2xl font-bold text-white">
                  {filteredSummary.received_total?.toFixed(0) || 0}€
                </div>
                <div className="text-xs text-blue-400">
                  of {filteredSummary.expected_total?.toFixed(0) || 0}€
                </div>
              </div>
            </div>

            {/* Client Status Filter + Payment Status Filter */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              {/* Client Status Filter Dropdown */}
              <div className="flex items-center gap-3">
                <label className="text-blue-300 text-sm font-medium">Show:</label>
                <select
                  value={clientStatusFilter}
                  onChange={(e) => {
                    setClientStatusFilter(e.target.value);
                    setFilter('all');
                  }}
                  className="px-4 py-2 rounded-lg bg-blue-900/40 text-white border border-blue-400/30 text-sm focus:ring-2 focus:ring-blue-400 cursor-pointer"
                  data-testid="client-status-filter"
                >
                  {CLIENT_STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value} className="bg-gray-800">
                      {option.label}
                    </option>
                  ))}
                </select>
                {clientStatusFilter !== 'all' && (
                  <button
                    onClick={() => setClientStatusFilter('all')}
                    className="text-blue-400 hover:text-blue-300 text-xs"
                  >
                    Clear filter
                  </button>
                )}
              </div>

              {/* Payment Status Filter Tabs */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    filter === 'all' ? 'bg-blue-500 text-white' : 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'
                  }`}
                >
                  All ({clientStatusFiltered?.length || 0})
                </button>
                {STATUS_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setFilter(option.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      filter === option.value
                        ? option.color.replace('/20', '').replace('text-', 'bg-').split(' ')[0] + ' text-white'
                        : option.color + ' hover:opacity-80'
                    }`}
                  >
                    {option.label} ({clientStatusFiltered?.filter(c => c.month_status === option.value).length || 0})
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-blue-400/30">
                <th className="text-left p-4 text-blue-200 font-medium">Client</th>
                <th className="text-left p-4 text-blue-200 font-medium">Status</th>
                <th className="text-left p-4 text-blue-200 font-medium">Mode</th>
                <th className="text-right p-4 text-blue-200 font-medium">Fee</th>
                <th className="text-center p-4 text-blue-200 font-medium">
                  <span className="text-yellow-300">{formatMonthDisplay(month)}</span>
                  <br/>
                  <span className="text-xs text-blue-300">Status</span>
                </th>
                <th className="text-center p-4 text-blue-200 font-medium">
                  <span className="text-yellow-300">{formatMonthDisplay(month)}</span>
                  <br/>
                  <span className="text-xs text-blue-300">Payment Date</span>
                </th>
                <th className="text-left p-4 text-blue-200 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-blue-300">
                    No clients to display
                  </td>
                </tr>
              ) : (
                filteredClients.map(client => (
                  <tr 
                    key={client.client_id} 
                    className="border-b border-blue-400/10 hover:bg-blue-900/20 transition"
                    data-testid={`accounting-row-${client.client_id}`}
                  >
                    <td className="p-4">
                      <div className="font-medium text-white">{client.client_name}</div>
                    </td>
                    <td className="p-4">
                      <span className={`text-sm px-2 py-1 rounded ${
                        client.client_status === 'active' ? 'bg-green-500/20 text-green-300' :
                        client.client_status === 'onboarding' ? 'bg-blue-500/20 text-blue-300' :
                        'bg-gray-500/20 text-gray-300'
                      }`}>
                        {formatClientStatus(client.client_status)}
                      </span>
                    </td>
                    <td className="p-4 text-blue-200 text-sm">
                      {formatBookingMode(client.booking_mode)}
                    </td>
                    <td className="p-4 text-right">
                      <span className="text-white font-medium">
                        {client.monthly_fee ? `${client.monthly_fee}€` : '-'}
                      </span>
                    </td>
                    {/* ERP Phase 4.8: Month-scoped status column */}
                    <td className="p-4">
                      <select
                        value={client.month_status}
                        onChange={(e) => handleStatusChange(client.client_id, e.target.value)}
                        disabled={updating === client.client_id}
                        className={`w-full px-3 py-2 rounded-lg text-sm font-medium cursor-pointer ${getStatusColor(client.month_status)} border-0 focus:ring-2 focus:ring-blue-400`}
                        data-testid={`status-select-${client.client_id}`}
                      >
                        {STATUS_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    {/* ERP Phase 4.8: Month-scoped payment date column */}
                    <td className="p-4">
                      {client.month_status === 'paid' ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="date"
                            value={client.payment_date || ''}
                            onChange={(e) => handlePaymentDateChange(client.client_id, e.target.value)}
                            disabled={updating === client.client_id}
                            className="flex-1 px-2 py-1.5 rounded-lg bg-blue-900/30 text-white border border-blue-400/30 text-sm focus:ring-2 focus:ring-blue-400"
                            data-testid={`payment-date-${client.client_id}`}
                          />
                          {client.payment_date && (
                            <button
                              onClick={() => handlePaymentDateChange(client.client_id, '')}
                              disabled={updating === client.client_id}
                              className="text-red-400 hover:text-red-300 px-1 text-sm"
                              title="Clear date"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-blue-400/50 text-sm">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={client.notes || ''}
                          onChange={(e) => {
                            // Update local state immediately for responsive UI
                            const newClients = data.clients.map(c => 
                              c.client_id === client.client_id ? {...c, notes: e.target.value} : c
                            );
                            setData({...data, clients: newClients});
                          }}
                          onBlur={(e) => handleNotesChange(client.client_id, e.target.value)}
                          placeholder="Notes..."
                          className="flex-1 px-3 py-2 rounded-lg bg-blue-900/20 text-blue-100 border border-blue-400/20 text-sm focus:ring-2 focus:ring-blue-400 placeholder-blue-400/40"
                          data-testid={`notes-input-${client.client_id}`}
                        />
                        {client.notes && (
                          <button
                            onClick={() => handleNotesChange(client.client_id, '')}
                            disabled={updating === client.client_id}
                            className="text-red-400 hover:text-red-300 px-1 text-sm"
                            title="Clear notes"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountingDashboard;
